import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getAllProductWaterUsageLCA,
  addProductWaterUsageLCA,
  updateProductWaterUsageLCA,
  deleteProductWaterUsageLCA,
} from "../../../../../../apis/Product/WaterUsageApi";
import { getProductWaterTypeDropdown } from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import { IconButton, Tooltip } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import InfoIcon from "app/NewProductCategories/ProductDetails/components/InfoIcon";
import TipBox from "components/TipBox";
import EditableResultDropdownAutocomplete from "../../../../../../components/EditableResultDropdownAutocomplete";
import { TableCompareTrigger } from "../../../components/lcaComparison";

// Impact categories configuration - All 16 categories
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  {
    name: "Photochemical ozone formation",
    unit: "kg NMVOC-eq",
    key: "smog",
  },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  {
    name: "Eutrophication, marine",
    unit: "kg N-eq",
    key: "eutrophication_marine",
  },
  {
    name: "Human toxicity, cancer effects",
    unit: "CTUh",
    key: "carcinogenics",
  },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  {
    name: "Particulate matter",
    unit: "disease incidence",
    key: "respiratory_effects",
  },
  {
    name: "Ecotoxicity, freshwater",
    unit: "CTUe",
    key: "ecotoxicity",
  },
  {
    name: "Resource use, energy carriers",
    unit: "MJ",
    key: "fossil_fuel_depletion",
  },
  { name: "Land use", unit: "Pt", key: "land_use" },
  {
    name: "Water use",
    unit: "m³ world-eq deprived",
    key: "water_use",
  },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  {
    name: "Ionising radiation, human health",
    unit: "kBq U235-eq",
    key: "ionising_radiation",
  },
];

const ProdWaterUsage = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});
  const [waterTypeDropdown, setWaterTypeDropdown] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] =
    useState(0);

  // Fetch water type dropdown data
  useEffect(() => {
    getProductWaterTypeDropdown({
      setDropdown: setWaterTypeDropdown,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    if (productId) {
      getAllProductWaterUsageLCA({
        productId,
        setObjects: setData,
      });
    }
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        // Backend returns water_type as code string
        const waterTypeCode =
          typeof row.water_type === "string"
            ? row.water_type
            : row.water_type?.value || row.water_type;
        if (waterTypeCode) {
          // Find water type by code
          const selectedWaterType = waterTypeDropdown.find(
            (option) =>
              !option.isCategory &&
              (option.code === waterTypeCode ||
                option.value === waterTypeCode ||
                option.name === waterTypeCode)
          );
          if (selectedWaterType) {
            newUnitMaterials[row.id] = selectedWaterType.unit || "m³";
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, waterTypeDropdown]);

  // Navigation handlers for impact categories
  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev > 0 ? prev - 1 : IMPACT_CATEGORIES.length - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev < IMPACT_CATEGORIES.length - 1 ? prev + 1 : 0
    );
  }, []);

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
  const currentImpactKey = currentImpactCategory.key;

  // Helper function to get matching option from dropdown
  const getMatchingOption = useCallback(
    (value) => {
      if (!value) return null;
      // If value is already an object, return it
      if (typeof value === "object") return value;
      // If value is a string (code), find matching option
      return (
        waterTypeDropdown.find(
          (option) =>
            !option.isCategory &&
            (option.code === value || option.value === value || option.name === value)
        ) || null
      );
    },
    [waterTypeDropdown]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "water_type",
        header: `${t("TYPE_WATER")}*`,
        type: "dropdown",
        isCategory: true,
        dropdownData: waterTypeDropdown,
        disableSortBy: true,
        width: "200px",
        unit: (rowId, rowData) => {
          const waterTypeCode =
            typeof rowData?.water_type === "string"
              ? rowData.water_type
              : rowData?.water_type?.value || rowData?.water_type;
          if (waterTypeCode) {
            const selectedWaterType = waterTypeDropdown.find(
              (option) =>
                !option.isCategory &&
                (option.code === waterTypeCode ||
                  option.value === waterTypeCode ||
                  option.name === waterTypeCode)
            );
            return selectedWaterType?.unit || unitMaterials[rowId] || "m³";
          }
          return unitMaterials[rowId] || "m³";
        },
      },
      {
        accessor: "qty",
        header: `${t("QTY_FUNC_UNIT")}*`,
        type: "number",
        disableSortBy: true,
        width: "250px",
        placeholder: t("USER_TYPES") || "user types...",
        unit: (rowId, rowData) => {
          const waterTypeCode =
            typeof rowData?.water_type === "string"
              ? rowData.water_type
              : rowData?.water_type?.value || rowData?.water_type;
          if (waterTypeCode) {
            const selectedWaterType = waterTypeDropdown.find(
              (option) =>
                !option.isCategory &&
                (option.code === waterTypeCode ||
                  option.value === waterTypeCode ||
                  option.name === waterTypeCode)
            );
            return selectedWaterType?.unit || unitMaterials[rowId] || "m³";
          }
          return unitMaterials[rowId] || "m³";
        },
      },
      {
        accessor: currentImpactKey,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              size="small"
              onClick={handlePreviousImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowLeft />
            </IconButton>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <span>{currentImpactCategory.name}</span>
              <span style={{ fontSize: "12px", color: "#666" }}>
                {currentImpactCategory.unit}
              </span>
            </div>
            <IconButton
              size="small"
              onClick={handleNextImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowRight />
            </IconButton>
            <TableCompareTrigger />
          </div>
        ),
        type: "custom",
        disableSortBy: true,
        width: "200px",
        optional: true, // Not required - calculated output only
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          // Backend returns impact categories directly on the object (e.g., global_warming)
          const impactValue =
            row.original[currentCategory.key] || // Direct field from API (e.g., global_warming)
            row.original[`impact_${currentCategory.key}`] || // Fallback for nested structure
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
      },
    ],
    [
      t,
      waterTypeDropdown,
      unitMaterials,
      currentImpactCategoryIndex,
      currentImpactCategory,
      currentImpactKey,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
    ]
  );

  const handleEdit = useCallback(
    (editedItem) => {
      // Get water type code from dropdown selection
      const waterTypeValue =
        editedItem.water_type?.value ||
        editedItem.water_type ||
        editedItem.input?.value ||
        editedItem.input;

      // Find water type to get code if value is name instead of code
      const foundWaterType = waterTypeDropdown?.find(
        (option) =>
          !option.isCategory &&
          (option.value === waterTypeValue ||
            option.code === waterTypeValue ||
            option.name === waterTypeValue)
      );

      // Use code field for API (backend expects water_type code)
      const waterTypeCode = foundWaterType?.code || waterTypeValue;

      updateProductWaterUsageLCA({
        productId,
        waterUsageId: editedItem.id,
        water_type: waterTypeCode, // Send code to backend
        qty: editedItem.qty,
        objects: data,
        setObjects: setData,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData, waterTypeDropdown, data]
  );

  const handleAdd = useCallback(
    (newItem) => {
      // Get water type code from dropdown selection
      const waterTypeValue =
        newItem.water_type?.value ||
        newItem.water_type ||
        newItem.input?.value ||
        newItem.input;

      // Find water type to get code if value is name instead of code
      const foundWaterType = waterTypeDropdown?.find(
        (option) =>
          !option.isCategory &&
          (option.value === waterTypeValue ||
            option.code === waterTypeValue ||
            option.name === waterTypeValue)
      );

      // Use code field for API (backend expects water_type code)
      const waterTypeCode = foundWaterType?.code || waterTypeValue;

      addProductWaterUsageLCA({
        productId,
        water_type: waterTypeCode, // Send code to backend
        qty: newItem.qty,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData, waterTypeDropdown]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteProductWaterUsageLCA({
        productId,
        waterUsageId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const disableRules = useMemo(() => {
    const rules = {};
    IMPACT_CATEGORIES.forEach((category) => {
      rules[category.key] = () => true; // All impact categories are read-only
    });
    return rules;
  }, []);

  // Dynamic placeholders to update unit when water type is selected
  const dynamicPlaceholders = useMemo(
    () => ({
      water_type: {
        targetField: "qty",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedWaterType = dropdownData?.find(
            (option) =>
              !option.isCategory &&
              (option.value === selectedValue ||
                option.code === selectedValue ||
                option.name === selectedValue)
          );
          if (selectedWaterType) {
            // For existing rows
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedWaterType.unit || "m³",
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedWaterType.unit || "m³", // Use a special key for new rows
              }));
            }
          }
        },
        dropdownDataKey: "water_type",
      },
    }),
    []
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={{
        water_type: waterTypeDropdown,
      }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    
      comparisonConfig={{ categories: IMPACT_CATEGORIES }}
    />
  );
};

export default ProdWaterUsage;
